import IndicatorVsQuoteChart from "components/charts/modern/IndicatorVsQuoteChart";
import MarketCapitalizationChart from "components/charts/modern/MarketCapitalizationChart";
import QuotesChart from "components/charts/modern/QuotesChart";
import {
  CompanyContainer,
  CompanyContentContainer,
  CompanyTabsContent,
  CompanyTabsList,
} from "components/company/v3/CompanyContainers";
import BrazilianHistoricalReturnContent from "components/company/v3/brazilian/contents/quote/BrazilianHistoricalReturnContent";
import useBrazilianIssuer from "components/company/v3/context/hooks/useBrazilianIssuer";
import { CompanyQuotesTabEnum } from "components/company/v3/menu/enums";
import classNames from "components/ui/classNames";
import { Tabs } from "components/ui/Tabs";
import { Provider } from "jotai";
import useSelfie from "lib/hooks/useSelfie";
import TickerSymbol from "lib/securities/TickerSymbol";
import notUndefinedOrNull from "lib/utils/notUndefinedOrNull";
import { DollarSignIcon, LineChartIcon, PercentIcon, TrendingUpIcon } from "lucide-react";
import { useIntl } from "react-intl";
import type { Updater } from "react-powerplug";
import { Value } from "react-powerplug";
import { ISecurityCountry } from "types/graphqlTypes";

interface BrazilianQuotesBoxProps {
  tickerSymbol: string;
  tab: CompanyQuotesTabEnum;
  onChangeTab: (tab: CompanyQuotesTabEnum) => void;
}

export default function BrazilianQuotesBox({ tickerSymbol, tab, onChangeTab }: BrazilianQuotesBoxProps) {
  const intl = useIntl();
  const issuer = useBrazilianIssuer();
  const viewer = useSelfie();

  const tickerSymbolsWithQuotes = issuer.securities.map((node) => node.tickerSymbol);
  const availableTickerSymbols = tickerSymbolsWithQuotes.filter((tickerSymbol) => {
    return !TickerSymbol.isUnitStockSymbol(tickerSymbol);
  });

  const hasAvailableTickerSymbols = availableTickerSymbols.length > 0;

  return (
    <CompanyContainer
      className={classNames({
        "xl:h-full xl:min-h-company-box": [CompanyQuotesTabEnum.QUOTE].includes(tab),
        "xl:h-fit": [CompanyQuotesTabEnum.HISTORICAL_RETURN].includes(tab),
      })}
    >
      <CompanyContentContainer className="grow">
        <Tabs
          defaultValue={CompanyQuotesTabEnum.QUOTE}
          value={tab}
          onValueChange={(value) => onChangeTab(value as CompanyQuotesTabEnum)}
          className="h-auto w-full"
        >
          <CompanyTabsList
            tabs={[
              {
                value: CompanyQuotesTabEnum.QUOTE,
                label: intl.formatMessage({
                  defaultMessage: "Cotação",
                  description: "Aba do menu superior da empresa",
                  id: "uWNGsP",
                }),
                icon: <LineChartIcon className="inline-flex" size={16} strokeWidth={2} />,
              },
              viewer?.isPremium
                ? {
                    value: CompanyQuotesTabEnum.HISTORICAL_RETURN,
                    label: intl.formatMessage({
                      defaultMessage: "Retorno Histórico",
                      description: "Aba do menu superior da empresa",
                      id: "oM9BVy",
                    }),
                    icon: <PercentIcon className="inline-flex" size={16} strokeWidth={2} />,
                  }
                : null,
              viewer?.isPremium
                ? {
                    value: CompanyQuotesTabEnum.PRICE_VS_INDICATOR,
                    label: intl.formatMessage({
                      defaultMessage: "Cotação x Lucro",
                      description: "Aba do menu superior da empresa",
                      id: "N3fHP+",
                    }),
                    icon: <TrendingUpIcon className="inline-flex" size={16} strokeWidth={2} />,
                  }
                : null,
              viewer?.isPremium
                ? {
                    value: CompanyQuotesTabEnum.MARKET_CAPITALIZATION,
                    label: intl.formatMessage({
                      defaultMessage: "Valor de Mercado",
                      description: "Aba do menu superior da empresa",
                      id: "VITY4x",
                    }),
                    icon: <DollarSignIcon className="inline-flex" size={16} strokeWidth={2} />,
                    disabled: !hasAvailableTickerSymbols,
                  }
                : null,
            ].filter(notUndefinedOrNull)}
          />

          <CompanyTabsContent value={CompanyQuotesTabEnum.QUOTE}>
            <Value<string[]>
              key={`QuotesCharts/${tickerSymbolsWithQuotes.join("")}`}
              initial={tickerSymbolsWithQuotes.filter((candidate) => candidate === tickerSymbol)}
            >
              {({ value, set }) => (
                <Provider>
                  <QuotesChart
                    tickerSymbols={value}
                    availableTickerSymbols={tickerSymbolsWithQuotes}
                    onToggleTickerSymbols={(tickerSymbols) => {
                      onToggleTickerSymbols(tickerSymbol, tickerSymbols, set);
                    }}
                  />
                </Provider>
              )}
            </Value>
          </CompanyTabsContent>

          <CompanyTabsContent value={CompanyQuotesTabEnum.HISTORICAL_RETURN}>
            <BrazilianHistoricalReturnContent />
          </CompanyTabsContent>

          <CompanyTabsContent value={CompanyQuotesTabEnum.PRICE_VS_INDICATOR}>
            <Value<string[]>
              key={`IndicatorVsQuoteChart/${tickerSymbolsWithQuotes.join("")}`}
              initial={tickerSymbolsWithQuotes.filter((candidate) => candidate === tickerSymbol)}
            >
              {({ value, set }) => (
                <Provider>
                  <IndicatorVsQuoteChart
                    country={ISecurityCountry.Br}
                    tickerSymbols={value}
                    availableTickerSymbols={tickerSymbolsWithQuotes}
                    onToggleTickerSymbols={(tickerSymbols) => {
                      onToggleTickerSymbols(tickerSymbol, tickerSymbols, set);
                    }}
                  />
                </Provider>
              )}
            </Value>
          </CompanyTabsContent>

          <CompanyTabsContent value={CompanyQuotesTabEnum.MARKET_CAPITALIZATION}>
            <Value<string[]>
              key={`MarketCapitalizationChart/${availableTickerSymbols.join("")}`}
              initial={[availableTickerSymbols[0]]}
            >
              {({ value, set }) => (
                <Provider>
                  <MarketCapitalizationChart
                    tickerSymbols={value}
                    availableTickerSymbols={availableTickerSymbols}
                    onToggleTickerSymbols={(tickerSymbols) =>
                      onToggleTickerSymbols(availableTickerSymbols[0], tickerSymbols, set)
                    }
                  />
                </Provider>
              )}
            </Value>
          </CompanyTabsContent>
        </Tabs>
      </CompanyContentContainer>
    </CompanyContainer>
  );
}

function onToggleTickerSymbols(tickerSymbol: string, tickerSymbols: string[], set: Updater<string[]>) {
  if (tickerSymbols.length === 0) {
    set([tickerSymbol]);
  } else {
    set(tickerSymbols);
  }
}
